import React, { useMemo, useState } from "react";
import cn from "classnames";
import styles from "./dashboards-list.module.scss";
import { Dashboard } from "@src/types/dashboards";
import { DashboardCard } from "@src/components/molecules/dashboard-card/dashboard-card";
import Spinner from "@src/components/atoms/spinner/spinner";
import { useI18N } from "@src/language/language-context";
import magnifyingGlassGray from "@src/assets/svg/magnifying-glass-gray.svg";

interface DashboardsListProps {
  dashboards: Dashboard[];
  selectedDashboardId: string | null;
  defaultDashboardId: string | null;
  isCollapsed: boolean;
  isCreating: boolean;
  /**
   * True while the parent is still fetching the list of dashboards. When
   * true, this component renders only a Spinner in place of the search bar
   * + list; the caller is responsible for showing the surrounding chrome
   * (page header, right column, etc.). Ponytail: don't ship a full-overlay
   * spinner from here — keep it scoped to this panel.
   */
  isLoading?: boolean;
  onSelectDashboard: (id: string) => void;
  onRenameDashboard: (
    id: string,
    name: string,
    description: string | null,
  ) => void;
  onDuplicateDashboard: (id: string) => void;
  onDeleteDashboard: (id: string) => void;
  onSetDefaultDashboard: (id: string) => void;
  onConfirmCreate: (name: string, description: string | null) => void;
  onCancelCreate: () => void;
}

const cssClassName = "dashboards-list";

// No-op function for disabled handlers
const noop = () => {
  // Intentionally empty - used for disabled event handlers during creation
};

export const DashboardsList = ({
  dashboards,
  selectedDashboardId,
  defaultDashboardId,
  isCollapsed,
  isCreating,
  isLoading,
  onSelectDashboard,
  onRenameDashboard,
  onDuplicateDashboard,
  onDeleteDashboard,
  onSetDefaultDashboard,
  onConfirmCreate,
  onCancelCreate,
}: DashboardsListProps) => {
  const i18n = useI18N();
  const [searchQuery, setSearchQuery] = useState("");

  // Filter by name + description (case-insensitive). Re-runs on every keystroke.
  const filteredDashboards = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return dashboards;
    return dashboards.filter(
      (d) =>
        d.name.toLowerCase().includes(query) ||
        (d.description ?? "").toLowerCase().includes(query),
    );
  }, [dashboards, searchQuery]);

  // Create a temporary dashboard for the creation UI
  const creatingDashboard = useMemo<Dashboard>(
    () => ({
      id: "temp-creating",
      name: "",
      description: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: "",
      last_updated_by: "",
      status: "draft" as const,
      is_default: false,
      columnCount: 24,
      widgets: {},
      positioning: {},
    }),
    [],
  );

  if (isLoading) {
    // Spinner lives inside this component so the surrounding chrome (page
    // header, right panel, library column) renders normally and we don't
    // blank the page on initial load.
    return (
      <div className={styles[cssClassName]}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles[cssClassName]}>
      {!isCollapsed && (
        <div className={styles[`${cssClassName}__search`]}>
          <img
            className={styles[`${cssClassName}__search-icon`]}
            src={magnifyingGlassGray}
            alt=""
          />
          <input
            type="text"
            className={styles[`${cssClassName}__search-input`]}
            placeholder={i18n("pages.dashboardsPage.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}
      <div
        className={cn(styles[`${cssClassName}__list`], {
          [styles[`${cssClassName}__list--collapsed`]]: isCollapsed,
        })}
      >
        {isCreating && (
          <DashboardCard
            dashboard={creatingDashboard}
            isSelected={false}
            isDefault={false}
            isCollapsed={isCollapsed}
            isCreating={true}
            onSelect={noop}
            onRename={noop}
            onDuplicate={noop}
            onDelete={noop}
            onSetDefault={noop}
            onConfirmCreate={onConfirmCreate}
            onCancelCreate={onCancelCreate}
          />
        )}

        {filteredDashboards.map((dashboard) => (
          <DashboardCard
            key={dashboard.id}
            dashboard={dashboard}
            isSelected={selectedDashboardId === dashboard.id}
            isDefault={defaultDashboardId === dashboard.id}
            isCollapsed={isCollapsed}
            onSelect={onSelectDashboard}
            onRename={onRenameDashboard}
            onDuplicate={onDuplicateDashboard}
            onDelete={onDeleteDashboard}
            onSetDefault={onSetDefaultDashboard}
          />
        ))}
      </div>
    </div>
  );
};
